/**
 * Command palette (⌘K / Ctrl+K, D-2604): jump to any console the session may see, run the win-ID lookup
 * (`WIN-XXXXX-XXXXX` or a pasted ID), and reach display / shortcut settings. Navigation commands come from the
 * same `visibleNav(session)` the sidebar uses, so permission gating is identical. Pure presentation: nothing
 * here calls the API.
 */
import { isValidWinId, normalizeWinId, formatWinId } from '@windrop/contracts';
import { ArrowRight, CornerDownLeft, Keyboard, Plus, Search, Settings2, Trophy } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../auth/session';
import { NAV_ICONS } from './icons';
import { SECTION_LABELS, visibleNav, type NavItem } from './navigation';

export interface PaletteCommand {
  id: string;
  label: string;
  hint?: string;
  group: string;
  icon?: ReactNode;
  keywords?: string;
  shortcut?: string;
  run: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenShortcuts: () => void;
  onOpenPreferences: () => void;
}

const matches = (q: string, c: PaletteCommand): boolean => {
  if (!q) return true;
  const hay = `${c.label} ${c.hint ?? ''} ${c.keywords ?? ''} ${c.group}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
};

export function CommandPalette({
  open,
  onOpenChange,
  onOpenShortcuts,
  onOpenPreferences,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const { session, can } = useSession();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const opener = useRef<Element | null>(null);
  const id = useId();

  const close = () => onOpenChange(false);

  const commands = useMemo<PaletteCommand[]>(() => {
    const nav: PaletteCommand[] = visibleNav(session).map((item: NavItem) => {
      const Icon = NAV_ICONS[item.id];
      return {
        id: `nav-${item.id}`,
        label: item.label,
        hint: item.hint,
        group: SECTION_LABELS[item.section],
        icon: Icon ? <Icon size={16} aria-hidden="true" /> : undefined,
        keywords: item.to,
        shortcut: item.shortcut ? `g ${item.shortcut}` : undefined,
        run: () => navigate(item.to),
      };
    });
    const actions: PaletteCommand[] = [];
    if (can('lottery.create'))
      actions.push({
        id: 'new-lottery',
        label: 'Create a lottery',
        hint: 'Open the creation wizard',
        group: 'Actions',
        icon: <Plus size={16} aria-hidden="true" />,
        keywords: 'new wizard',
        run: () => navigate('/lotteries/new'),
      });
    actions.push(
      {
        id: 'prefs',
        label: 'Display preferences',
        hint: 'Theme, calm mode, sound',
        group: 'Actions',
        icon: <Settings2 size={16} aria-hidden="true" />,
        keywords: 'theme dark light calm',
        run: onOpenPreferences,
      },
      {
        id: 'shortcuts',
        label: 'Keyboard shortcuts',
        hint: 'Every shortcut on one card',
        group: 'Actions',
        icon: <Keyboard size={16} aria-hidden="true" />,
        shortcut: '?',
        run: onOpenShortcuts,
      },
    );
    return [...nav, ...actions];
  }, [session, can, navigate, onOpenPreferences, onOpenShortcuts]);

  const winLookup = useMemo<PaletteCommand | null>(() => {
    if (!can('winners.lookup')) return null;
    const normalized = normalizeWinId(query.trim());
    if (!normalized || !isValidWinId(normalized)) return null;
    return {
      id: 'win-lookup',
      label: `Look up ${formatWinId(normalized)}`,
      hint: 'Opens the winner record; mismatches are reported, never guessed',
      group: 'Win ID',
      icon: <Trophy size={16} aria-hidden="true" />,
      run: () => navigate(`/winners/lookup/${normalized}`),
    };
  }, [query, can, navigate]);

  const visible = useMemo(() => {
    const list = winLookup ? [winLookup] : [];
    return [...list, ...commands.filter((c) => matches(query, c))];
  }, [commands, winLookup, query]);

  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    setQuery('');
    setActive(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      const el = opener.current;
      if (el instanceof HTMLElement && el.isConnected) el.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, visible.length]);

  if (!open) return null;

  const run = (c: PaletteCommand) => {
    close();
    c.run();
  };

  let lastGroup = '';
  return (
    <div
      className="wdc-palette__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className="wdc-palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            // Keep focus on the input: the list is driven by arrow keys (role=listbox).
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
      >
        <div className="wdc-palette__input">
          <Search size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-activedescendant={visible[active] ? `${id}-opt-${visible[active]!.id}` : undefined}
            aria-autocomplete="list"
            aria-label="Jump to a console, action or win ID"
            placeholder="Jump to… or paste a win ID"
            value={query}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((i) => Math.min(visible.length - 1, i + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((i) => Math.max(0, i - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const c = visible[active];
                if (c) run(c);
              }
            }}
          />
          <kbd>Esc</kbd>
        </div>
        {visible.length === 0 ? (
          <p className="wdc-palette__empty">
            Nothing matches “{query}”. Try a console name or a win ID such as WIN-XXXXX-XXXXX.
          </p>
        ) : (
          <ul id={`${id}-list`} role="listbox" className="wdc-palette__list" ref={listRef}>
            {visible.map((c, i) => {
              const showGroup = c.group !== lastGroup;
              lastGroup = c.group;
              return (
                <li key={c.id} role="presentation">
                  {showGroup ? (
                    <div className="wdc-palette__group" aria-hidden="true">
                      {c.group}
                    </div>
                  ) : null}
                  <div
                    id={`${id}-opt-${c.id}`}
                    role="option"
                    aria-selected={i === active}
                    data-index={i}
                    className="wdc-palette__item"
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => run(c)}
                  >
                    {c.icon ?? <ArrowRight size={16} aria-hidden="true" />}
                    <span>
                      {c.label}
                      {c.hint ? <span className="wd-muted"> · {c.hint}</span> : null}
                    </span>
                    {c.shortcut ? (
                      <kbd aria-label={`shortcut ${c.shortcut}`}>{c.shortcut}</kbd>
                    ) : i === active ? (
                      <kbd aria-hidden="true">
                        <CornerDownLeft size={11} />
                      </kbd>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="wdc-palette__foot" aria-hidden="true">
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>g</kbd> + key jumps from anywhere
          </span>
        </div>
      </div>
    </div>
  );
}
